import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  constants,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { Button, Icon, Label, Loader, MoneyInput } from '@atlas/ui';

import { useBonusesContext } from 'src/components/BonusesProvider';
import QuickDepositPaymentMethod from 'src/components/QuickDepositPopup/QuickDepositPaymentMethod';
import useFormatter from 'src/hooks/useFormatter';

import { useDepositSubmit } from './hooks/useDepositSubmit';
import { useInitialPayment } from './hooks/useInitialPayment';
import QuickDepositBonuses from './QuickDepositBonuses';
import QuickDepositPaymentMethodDropdown from './QuickDepositPaymentMethodDropdown';

export const BUTTON_POSITION = {
  BOTTOM: 'bottom',
  INLINE: 'inline',
};

const QuickDepositFormContent = ({
  buttonPosition = BUTTON_POSITION.INLINE,
  closeQRCodeOnSuccess,
  onClose,
  onDepositLoaderShown,
  onDepositRedirectSuccess,
  onDepositStart,
  onDepositSuccess,
  setRedirectUrl,
  titleType,
}) => {
  const {
    wallet: { currency },
  } = useApplicationState();

  const history = useHistory();
  const { currencyFormatter } = useFormatter();

  const [isInputActive, setIsInputActive] = useState(false);
  const [value, setValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.DESKTOP,
  );
  const { initialValue, paymentMethod, setPaymentMethod } = useInitialPayment(
    paymentMethods,
    currency,
  );
  const { setDepositAmount, setPaymentMethod: setBonusesPaymentMethod } = useBonusesContext();

  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const { browser, ip, locale, os } = useDetection();

  useEffect(() => {
    if (initialValue && value === '') {
      setValue(initialValue);
      setDepositAmount(initialValue);
    }
  }, [initialValue, setDepositAmount, value]);

  const { error, handleDeposit, loading, setError } = useDepositSubmit({
    browser,
    closeQRCodeOnSuccess,
    history,
    ip,
    locale,
    onClose,
    onDepositLoaderShown,
    onDepositRedirectSuccess,
    onDepositStart,
    onDepositSuccess,
    os,
    paymentMethod,
    setRedirectUrl,
    showInPopup,
    value,
  });

  const clampValueToLimits = useCallback(() => {
    if (!paymentMethod || !value) return;
    const numValue = Number(value);

    if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
      setDepositAmount(paymentMethod.maxAmount);
      setError(null);
    } else if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
      setDepositAmount(paymentMethod.minAmount);
      setError(null);
    }
  }, [paymentMethod, setDepositAmount, setError, value]);

  const prevPaymentMethodRef = useRef(paymentMethod);

  useEffect(() => {
    if (prevPaymentMethodRef.current === paymentMethod) return;
    prevPaymentMethodRef.current = paymentMethod;
    clampValueToLimits();
  }, [paymentMethod, clampValueToLimits]);

  useEffect(() => {
    if (paymentMethod) {
      setBonusesPaymentMethod(paymentMethod);
    }
  }, [paymentMethod, setBonusesPaymentMethod]);

  const handleValueChange = newValue => {
    setDepositAmount(newValue);
    setValue(newValue);
    if (error) setError(null);
  };

  const handleBlur = useCallback(() => {
    setIsInputActive(false);
    clampValueToLimits();
  }, [clampValueToLimits]);

  const disabledDepositButton = !value || loading;

  return (
    <div className="quick-deposit-content">
      {paymentMethod ? (
        <>
          <div className="quick-deposit-form">
            {isDropdownOpen && paymentMethods.length > 1 && (
              <QuickDepositPaymentMethodDropdown
                onSelect={pm => {
                  setPaymentMethod(pm);
                  setBonusesPaymentMethod(pm);
                  setIsDropdownOpen(false);
                }}
                paymentMethod={paymentMethod}
                paymentMethods={paymentMethods}
              />
            )}
            <div className="quick-deposit-input">
              <QuickDepositPaymentMethod
                isDropdownOpen={isDropdownOpen}
                paymentMethod={paymentMethod}
                setIsDropdownOpen={setIsDropdownOpen}
                withDropdown={paymentMethods.length > 1}
              />
              <MoneyInput
                active={isInputActive}
                currency={currency}
                error={error}
                label="amount"
                onBlur={handleBlur}
                onChange={handleValueChange}
                onFocus={() => setIsInputActive(true)}
                value={value}
              />
              {buttonPosition === BUTTON_POSITION.INLINE && (
                <Button
                  className="deposit-button"
                  disabled={disabledDepositButton}
                  label="deposit"
                  loading={loading}
                  onClick={handleDeposit}
                  size="big"
                />
              )}
            </div>
            {error && (
              <div className="quick-deposit-form-error">
                <Icon className="error-icon" height={18} name="error" width={18} />
                <Label className="error-message" message={error.message} />
              </div>
            )}
          </div>

          <QuickDepositBonuses currencyFormatter={currencyFormatter} titleType={titleType} />

          {buttonPosition === BUTTON_POSITION.BOTTOM && (
            <Button
              className="deposit-button"
              disabled={disabledDepositButton}
              label="deposit"
              loading={loading}
              onClick={handleDeposit}
              size="big"
            />
          )}
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
};

QuickDepositFormContent.propTypes = {
  buttonPosition: PropTypes.oneOf(['inline', 'bottom']),
  closeQRCodeOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onDepositLoaderShown: PropTypes.func,
  onDepositRedirectSuccess: PropTypes.func,
  onDepositStart: PropTypes.func,
  onDepositSuccess: PropTypes.func,
  setRedirectUrl: PropTypes.func,
  titleType: PropTypes.string,
};

const QuickDepositForm = props => {
  return <QuickDepositFormContent {...props} />;
};

QuickDepositForm.propTypes = QuickDepositFormContent.propTypes;

export default QuickDepositForm;
